// client/src/components/UserOnboarding.tsx
import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Check, MapPin, Sprout, Bell } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface OnboardingProps {
  onComplete: () => void;
  user: any;
}

export default function UserOnboarding({ onComplete, user }: OnboardingProps) {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    farmName: '',
    location: '',
    farmSize: '',
    primaryCrops: [],
    experience: '',
    notifications: true
  });

  const steps = [
    {
      title: language === 'st' ? 'Rea ho AgroSense!' : 'Welcome to AgroSense!',
      content: language === 'st' 
        ? 'Re tla u thusa ho laola polasi ea hau ka bohlale. A re qaleng!'
        : 'We\'ll help you manage your farm intelligently. Let\'s get started!',
      icon: <Sprout className="w-12 h-12 text-green-600" />
    },
    {
      title: language === 'st' ? 'Tlhahiso ea Polasi' : 'Farm Information',
      content: language === 'st' 
        ? 'Re hloka lintlha tse ling mabapi le polasi ea hau'
        : 'We need some details about your farm',
      icon: <MapPin className="w-12 h-12 text-green-600" />
    },
    {
      title: language === 'st' ? 'Litemoso' : 'Notifications',
      content: language === 'st'
        ? 'Khetha mofuta oa litemoso tseo u li batlang'
        : 'Choose what notifications you\'d like to receive',
      icon: <Bell className="w-12 h-12 text-green-600" />
    }
  ];

  const crops = [
    { id: 'maize', name: language === 'st' ? 'Poone' : 'Maize' },
    { id: 'sorghum', name: language === 'st' ? 'Mabele' : 'Sorghum' },
    { id: 'wheat', name: language === 'st' ? 'Koro' : 'Wheat' },
    { id: 'beans', name: language === 'st' ? 'Linaoa' : 'Beans' },
    { id: 'vegetables', name: language === 'st' ? 'Meroho' : 'Vegetables' }
  ];

  const handleNext = async () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      await handleComplete();
    }
  };

  const [isCompleting, setIsCompleting] = useState(false);

  const handleComplete = async () => {
    setIsCompleting(true);
    
    try {
      // Import supabase
      const { supabase } = await import('../lib/supabaseClient');
      
      // Save farm to Supabase if farm data provided
      if (formData.farmName.trim()) {
        const { data, error: farmError } = await supabase
          .from('farms')
          .insert({
            user_id: user.id,
            name: formData.farmName.trim(),
            location: formData.location?.trim() || null,
            area: formData.farmSize ? parseFloat(formData.farmSize) : null
          })
          .select();
        
        if (farmError) {
          console.error('Farm creation error:', farmError);
          throw farmError;
        }
        
        console.log('Farm created successfully:', data);
      }
      
      // Mark onboarding as completed
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
      
    } catch (error) {
      console.error('Onboarding error:', error);
      // Still mark as completed to not block user
      localStorage.setItem(`onboarding_completed_${user.id}`, 'true');
    } finally {
      setIsCompleting(false);
      onComplete();
    }
  };

  const toggleCrop = (cropId: string) => {
    setFormData(prev => ({
      ...prev,
      primaryCrops: prev.primaryCrops.includes(cropId)
        ? prev.primaryCrops.filter(id => id !== cropId)
        : [...prev.primaryCrops, cropId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
        {/* Progress bar */}
        <div className="flex mb-6">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`flex-1 h-2 rounded-full mx-1 ${
                index <= step ? 'bg-green-600' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            {steps[step].icon}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {steps[step].title}
          </h2>
          <p className="text-gray-600">
            {steps[step].content}
          </p>
        </div>

        {/* Step-specific forms */}
        {step === 1 && (
          <div className="space-y-4">
            <input
              type="text"
              placeholder={language === 'st' ? 'Lebitso la polasi' : 'Farm name'}
              value={formData.farmName}
              onChange={(e) => setFormData(prev => ({ ...prev, farmName: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg"
            />
            <input
              type="text"
              placeholder={language === 'st' ? 'Sebaka' : 'Location'}
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg"
            />
            <input
              type="number"
              placeholder={language === 'st' ? 'Boholo ba polasi (li-hectare)' : 'Farm size (hectares)'}
              value={formData.farmSize}
              onChange={(e) => setFormData(prev => ({ ...prev, farmSize: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg"
            />
            <div>
              <p className="mb-2 font-medium">
                {language === 'st' ? 'Lijalo tsa mantlha:' : 'Primary crops:'}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {crops.map(crop => (
                  <button
                    key={crop.id}
                    onClick={() => toggleCrop(crop.id)}
                    className={`p-2 rounded-lg border text-sm ${
                      formData.primaryCrops.includes(crop.id)
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : 'bg-gray-50 border-gray-300'
                    }`}
                  >
                    {crop.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.notifications}
                onChange={(e) => setFormData(prev => ({ ...prev, notifications: e.target.checked }))}
                className="w-5 h-5 text-green-600"
              />
              <span>
                {language === 'st' 
                  ? 'Fumana litemoso tsa boemo ba leholimo le likokoanyana'
                  : 'Receive weather and pest alerts'
                }
              </span>
            </label>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={() => setStep(step - 1)}
            disabled={step === 0}
            className="flex items-center px-4 py-2 text-gray-600 disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {language === 'st' ? 'Morao' : 'Back'}
          </button>
          
          <button
            onClick={handleNext}
            disabled={isCompleting}
            className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCompleting ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {language === 'st' ? 'E ntse e sebetsa...' : 'Completing...'}
              </>
            ) : step === steps.length - 1 ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                {language === 'st' ? 'Qetela' : 'Complete'}
              </>
            ) : (
              <>
                {language === 'st' ? 'Pele' : 'Next'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}